/**
 * Converts optional arrays into a safe empty array for normalization.
 */
export function safeArray<T>(value: T[] | undefined | null): T[] {
  return Array.isArray(value) ? value : [];
}

/**
 * Trims and caps text so prompt inputs stay compact and useful.
 */
export function truncate(value: string | undefined, max = 1000): string | undefined {
  if (!value) return undefined;
  const normalized = value.replace(/\s+/g, " ").trim();
  if (!normalized) return undefined;
  return normalized.length <= max ? normalized : normalized.slice(0, max);
}

/**
 * Removes noisy URLs from social text before sending it to prompts.
 */
export function stripUrls(value: string | undefined, max = 1000): string | undefined {
  if (!value) return undefined;
  return truncate(value.replace(/https?:\/\/\S+/g, "").replace(/www\.\S+/g, "").trim(), max);
}

/**
 * Recursively removes empty fields (null, undefined, empty strings, empty arrays, empty objects) 
 * from an object to keep it compact.
 */
export function compactObject<T extends Record<string, any>>(obj: T): T {
  const output: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    const cleanedValue = cleanValue(value);
    if (!isEmptyValue(cleanedValue)) output[key] = cleanedValue;
  }

  return output as T;
}

function cleanValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(cleanValue).filter((item) => !isEmptyValue(item));
  }

  if (value && typeof value === "object") {
    return compactObject(value as Record<string, any>);
  }

  if (typeof value === "string") {
    return value.trim();
  }

  return value;
}

function isEmptyValue(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === "string") return value.length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
}
